import { Building2, Download, FileJson, FileSpreadsheet, Loader2, Plus, Upload } from "lucide-react";
import { Button } from "@/shared/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import type { PropertyExportFormat } from "@/features/exports/services";

interface Props {
  clientLogo?: string | null;
  clientName?: string;
  totalCount: number;
  /** Portfolio delivered % (delivered / total). Omitted → no chip. */
  deliveredPercent?: number | null;
  exportsEnabled: boolean;
  exportingFormat: PropertyExportFormat | null;
  onExport: (format: PropertyExportFormat) => void;
  onMoreExports: () => void;
  onAddProperty: () => void;
  onImportCSV?: () => void;
}

export function PropertiesHeader({
  clientLogo,
  clientName,
  totalCount,
  deliveredPercent,
  exportsEnabled,
  exportingFormat,
  onExport,
  onMoreExports,
  onAddProperty,
  onImportCSV,
}: Props) {
  return (
    <div className="glass-panel flex flex-shrink-0 items-center gap-3 rounded-xl px-4 py-2.5 mb-3">
      <div className="flex h-7 w-7 items-center justify-center overflow-hidden rounded-lg bg-muted">
        {clientLogo ? (
          <img src={clientLogo} alt={clientName ?? "Logo"} className="h-full w-full object-cover" />
        ) : (
          <Building2 className="h-4 w-4 text-muted-foreground" />
        )}
      </div>
      <span className="text-[17px] font-bold text-foreground">Properties</span>
      <span className="inline-flex items-baseline gap-1 rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground">
        <span className="font-semibold text-foreground">{totalCount.toLocaleString()}</span>
        total
      </span>
      {deliveredPercent != null && (
        <span className="inline-flex items-baseline gap-1 rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-xs text-emerald-400">
          <span className="font-semibold">{deliveredPercent}%</span>
          delivered
        </span>
      )}

      <div className="ml-auto flex items-center gap-2">
        {exportsEnabled && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={exportingFormat !== null}>
                {exportingFormat !== null ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                <span>Export</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onExport("xlsx")}>
                <FileSpreadsheet className="mr-2 h-4 w-4" /> Property list (XLSX)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onExport("csv")}>
                <Download className="mr-2 h-4 w-4" /> Property list (CSV)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onExport("json")}>
                <FileJson className="mr-2 h-4 w-4" /> Property list (JSON)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onMoreExports}>
                <Building2 className="mr-2 h-4 w-4" /> More exports →
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {onImportCSV && (
          <Button variant="outline" size="sm" onClick={onImportCSV}>
            <Upload className="h-4 w-4" /> Import
          </Button>
        )}
        <Button variant="default" size="sm" onClick={onAddProperty}>
          <Plus className="h-4 w-4" /> Add
        </Button>
      </div>
    </div>
  );
}
